<script lang="ts" setup>
import { Empty } from 'ant-design-vue'
import type { CityRoundByMapAndMilesPageListVO, FloorDetailIndexRoundVO } from '~/server/newhouse/interface'
import { getOptionList, getSignUpConfig } from '~/server/common'

defineOptions({
  name: 'InfoList',
})
const props = defineProps<{
  /**
   * 城市id
   */
  cityId: number
  /**
   * 楼盘地址
   */
  address?: string
  roundList: FloorDetailIndexRoundVO[]
  roundDataList: CityRoundByMapAndMilesPageListVO[]
  currentRoundId: number
  isFilleScreen: boolean
}>()
const emits = defineEmits(['toggleCurrentRoundId', 'toggleFullScreen'])
const route = useRoute()
const { commonCollect } = userInfoAction()
const floorInfo: any = inject('floorInfo')
/**
 *  currentRound的对应关系
 *  1 商业
 *  2 医疗
 *  3 景点
 *  4 交通
 *  5 教育
 */
const currentRound = defineModel('currentRound', { type: Number, default: 1 })
const roundItemListRef = ref()

watch(() => route.query.currentRound, (newVal, oldVal) => {
  if (newVal && newVal !== oldVal)
    handleClick(Number(newVal))
}, { immediate: true })
/**
 * 周边配套类别 图标
 */
const roundIconMap: { [key: number]: string } = {
  1: 'https://imgf1.fccs.com.cn/newhouse-mobile/around/icon1.png',
  2: 'https://imgf1.fccs.com.cn/newhouse-mobile/around/icon2.png',
  3: 'https://imgf1.fccs.com.cn/newhouse-mobile/around/icon3.png',
  4: 'https://imgf1.fccs.com.cn/newhouse-mobile/around/icon4.png',
  5: 'https://imgf1.fccs.com.cn/newhouse-mobile/around/icon5.png',
}

function handleClick(val: number) {
  currentRound.value = val
  emits('toggleCurrentRoundId')
}

watch(() => props.currentRoundId, (roundId) => {
  const roundIndex = props.roundDataList.findIndex(item => item.roundId === roundId)
  if (roundIndex > -1)
    roundItemListRef.value[roundIndex].scrollIntoView({ behavior: 'smooth', block: 'center' })
})

/**
 * 获取 咨询问题列表
 */
const { data: askOptionList } = useAsyncData(() => getOptionList({
  cityId: props.cityId,
  optionTypeCode: 'appCityRoundAskQuestion',
}), {
  transform(res) {
    return res.dataBody || []
  },
})

/**
 * 咨询轮播 当前显示的索引
 */
let currentQuestionIndex = 0

/**
 * 当前正在显示的轮播问题
 */
const currentQuestion = computed(() => {
  const item = askOptionList.value?.find((_, index: number) => index === currentQuestionIndex)
  return item || {}
})

/**
 * 后面做微聊应该会删掉
 */
function onConsultingService() {
  if (currentQuestion.value) {
    handleCollect({
      configCode: 'pcSurroundingFacilitiesPage',
      message: currentQuestion.value.optionValue,
    })
  }
}
/**
 * 获取报名配置
 */
const { data: configMap } = useAsyncData(() => getSignUpConfig({
  configCodes: ['pcSurroundingFacilitiesPage'].join(','),
}), {
  transform: (res: any) => {
    if (res.code === 0) {
      return res?.dataBody?.reduce((acc, item) => {
        acc[item.configCode] = item
        return acc
      }, {})
    }
    else {
      return {}
    }
  },
})
// 咨询置业顾问
async function handleCollect(info: any) {
  const config = configMap?.value[info?.configCode] || {}
  await commonCollect({
    floorId: floorInfo.value.floorId,
    configId: config?.configId,
    source: '新房/PC/周边配套',
    adviserList: floorInfo.value.floorAdviserList || floorInfo.value.adviserList,
    ...info,
  })
}
/**
 * 咨询轮播
 */

function onChange(current: number) {
  currentQuestionIndex = current
}

/**
 * 空状态图片
 */
const simpleImage = Empty.PRESENTED_IMAGE_SIMPLE
</script>

<template>
  <div class="top">
    <span class="address">{{ address ? '楼盘地址' : '' }}</span>
    <div class="cursor-pointer" @click="emits('toggleFullScreen')">
      <FullscreenExitOutlined v-if="isFilleScreen" class="full" />
      <FullscreenOutlined v-else class="full" />
      <span class="screen">全屏</span>
    </div>
  </div>
  <div v-if="address" class="address-info">
    {{ address }}
  </div>
  <div class="icons">
    <div v-for="round in roundList" :key="round.key" class="icon-item" :class="{ active: currentRound === round.key }" @click="handleClick(round.key)">
      <img :src="roundIconMap[round.key]">
      <span class="icon-text">{{ round.name }}</span>
    </div>
  </div>
  <div class="map-list">
    <template v-if="roundDataList && roundDataList.length">
      <div v-for="item in roundDataList" :key="item.roundId" ref="roundItemListRef" class="list-item cursor-pointer" :class="{ active: item.roundId === currentRoundId }" @click="emits('toggleCurrentRoundId', item.roundId)">
        <div class="item-top">
          <span v-if="item.roundLabel" class="label">  {{ item.roundLabel }}</span>
          <span class="area">
            {{ item.name }}
          </span>
          <span class="distance"> {{ item.distance }}{{ item.distanceUnit }}</span>
        </div>
        <div class="item-bottom">
          {{ item.address }}
        </div>
      </div>
    </template>
    <!--    <Empty v-else /> -->
    <a-empty v-else :image="simpleImage" class="mt-100px mb-100px" />
  </div>

  <div v-if="askOptionList && askOptionList.length" class="question-bot-menu">
    <img src="https://imgf1.fccs.com.cn/newhouse-mobile/around/session.png" class="avatar">
    <a-carousel :autoplay="true" vertical :autoplay-speed="5000" :dots="false" class="question" :after-change="onChange">
      <div v-for="(item, index) in askOptionList" :key="index" class="question-area">
        {{ item.optionValue }}
      </div>
    </a-carousel>
    <div class="need-question" @click="onConsultingService">
      咨询
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .top {
    display: flex;
    align-items: center;

    .address {
      flex: 1;
      font-size: 22px;
      font-weight: bold;
    }

    .full {
      margin-right: 8px;
      font-size: 16px;
      color: #3D3D3D;
    }

    .screen {
      color: #3D3D3D;
      font-size: 16px;
    }
  }
  .address-info {
    font-size: 16px;
    margin-top: 24px;
    color: #3D3D3D;
  }

  .icons {
    margin-top: 20px;
    display: flex;
    justify-content: space-around;

    .icon-item {
      display: flex;
      flex-flow: column;
      cursor: pointer;
      img{
        width: 30px;
        height: 36.5px;
      }
      &.active {
        .icon-text {
          font-weight: bold;
        }
      }
    }

    .icon-img {
      width: 30px;
      height: 36px;
    }

    .icon-text {
      margin-top: 8px;
      font-size: 14px;
      color: #3D3D3D;
    }

  }

  .map-list {
    flex: 1;
    margin-top: 20px;
    margin-bottom: 20px;
    height: 200px;
    overflow: hidden;
    scrollbar-width: thin; /* 设置滚动条为细模式 */
    //padding-right: 11px;
    &:hover {
      //padding-right: 0;
      overflow-y: auto;
    }

    //overflow-y: auto;
    .list-item {
      width: 305px;
      padding: 10px 5px 0;
      border-bottom: 1px solid rgba(0, 0, 0, 0.05);

      &.active {
        border-radius: 5px;
        background-color: rgba(0, 0, 0, 0.02);
      }

      .item-top {
        $lh: 28px;
        margin-bottom: 9px;
        line-height: $lh;

        .label{
          display: inline-block;
          border: 0.5px solid #E1E1E1;
          background: #F9F9F9;
          padding: 0 5px;
          height: 22px;
          line-height: 22px;
          font-size: 14px;
          color: #616669;
          box-sizing: border-box;
          border-radius: 3px;
        }
        .area{
          margin-left: 6px;
          font-weight: bold;
          font-size: 16px;
          color: #3D3D3D;
        }
        .distance{
          font-size: 14px;
          font-weight: bold;
          color: #3D3D3D;
          float: right;
        }
      }

      .item-bottom {
        margin-bottom: 12px;
        font-size: 14px;
        color: #9C9C9C;
      }

      &:last-child {
        border-bottom: none;
      }
    }
  }
  .question-bot-menu{
    display: flex;
    padding-left: 10px;
    height: 40px;
    align-items: center;
    background: #F3FAF4;
    border: 1px solid #CBEECB;
    border-radius: 5px;
    overflow: hidden;

    .question{
      flex: 1;
      height: 38px;
      line-height: 38px;
      padding-right: 8px;
      overflow: hidden;

      &-area {
        color: #3D3D3D;
        font-size: 16px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        cursor: default;
      }
    }
    .need-question{
      background: #52BE54;
      box-shadow: 0 0 1px 0 rgba(238, 112, 70, 0.3);
      color: #FFFFFF;
      font-size: 18px;
      box-sizing: border-box;
      padding: 0 18px;
      height: 40px;
      line-height: 40px;
      text-align: center;

      cursor: pointer;
    }
    .avatar{
      width: 16px;
      height: 16px;
      margin-right: 10px;
    }
  }
</style>
